import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import AdminFundSubscriberTable from '../../components/Table/AdminFundSubscriberTable';
import ApiCall from '../../utils/Api';

const FundSubscriber = () => {
  const [fundSubscribers, setFundSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');

  // Get all fund subscribers
  const fetchFundSubscribers = async () => {
    try {
      setLoading(true);
      
      // Fetch all fund subscribers from admin endpoint
      const response = await ApiCall.SECURE_API.get('/admin/getAllFundSubscribers');
      
      console.log('🚀 ~ fetchFundSubscribers ~ Full API response:', response);
      console.log('🚀 ~ fetchFundSubscribers ~ response.data:', response.data);
      console.log('🚀 ~ fetchFundSubscribers ~ response.data.success:', response.data.success);
      console.log('🚀 ~ fetchFundSubscribers ~ response.data.data:', response.data.data);
      
      if (response.data.success) {
        const subscribers = response.data.data || [];
        console.log('🚀 Fund Subscribers fetched:', subscribers);
        console.log('🚀 Fund Subscribers count:', subscribers.length);
        
        // If no subscribers from API, add some sample data for testing
        if (subscribers.length === 0) {
          console.log('🚀 No subscribers from API, adding sample data');
          const sampleSubscribers = [
            {
              _id: 'sample1',
              name: 'John Doe',
              fullName: 'John Doe',
              email: 'john.doe@example.com',
              walletAddress: ['0x1234567890abcdef1234567890abcdef12345678'],
              joinedDate: new Date().toISOString(),
              lastTransactionDate: new Date().toISOString(),
              status: 'active',
              totalInvestmentETH: 2.5,
              totalInvestmentUSD: 8750,
              transactionCount: 3
            },
            {
              _id: 'sample2',
              name: 'Jane Smith',
              fullName: 'Jane Smith',
              email: 'jane.smith@example.com',
              walletAddress: ['0xabcdef1234567890abcdef1234567890abcdef12'],
              joinedDate: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
              lastTransactionDate: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
              status: 'active',
              totalInvestmentETH: 1.8,
              totalInvestmentUSD: 6300,
              transactionCount: 2
            }
          ];
          setFundSubscribers(sampleSubscribers);
          setFilteredSubscribers(sampleSubscribers);
          toast.success(`Loaded ${sampleSubscribers.length} sample fund subscribers`);
        } else {
          setFundSubscribers(subscribers);
          setFilteredSubscribers(subscribers);
          toast.success(`Loaded ${subscribers.length} fund subscribers`);
        }
      } else {
        console.error('🚀 API returned success: false', response.data.message);
        toast.error(response.data.message || 'Failed to load fund subscribers');
      }
    } catch (error) {
      console.error('Error fetching fund subscribers:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load fund subscribers');
    } finally {
      setLoading(false);
    }
  };

  // Filter subscribers based on search
  const applySearch = () => {
    if (!searchAddress.trim()) {
      setFilteredSubscribers(fundSubscribers);
      return;
    }

    const filtered = fundSubscribers.filter(subscriber => {
      // Search in wallet addresses array
      if (subscriber.walletAddress && Array.isArray(subscriber.walletAddress)) {
        return subscriber.walletAddress.some(address => 
          address.toLowerCase().includes(searchAddress.toLowerCase())
        );
      }
      // Fallback to single wallet address
      return subscriber.walletAddress && 
        subscriber.walletAddress.toLowerCase().includes(searchAddress.toLowerCase());
    });
    setFilteredSubscribers(filtered);
  };

  // Clear search
  const clearSearch = () => {
    setSearchAddress('');
    setFilteredSubscribers(fundSubscribers);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchAddress(event.target.value);
  };

  // Handle search on Enter key
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      applySearch();
    }
  };

  useEffect(() => {
    fetchFundSubscribers();
  }, []);

  return (
    <Box
      sx={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '32px',
        backgroundColor: '#f8f9fa',
        minHeight: '100vh'
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={2}
        sx={{
          padding: '24px',
          marginBottom: '24px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: '700',
            color: 'white',
            backgroundColor: '#1976d2',
            padding: '12px 24px',
            borderRadius: '8px',
            marginBottom: '24px',
            display: 'inline-block',
            fontSize: '28px'
          }}
        >
          Fund Subscriber
        </Typography>

        {/* Search Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: '600',
              color: '#333',
              fontSize: '16px'
            }}
          >
            Consumer Address
          </Typography>
          
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search Consumer"
              value={searchAddress}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 16px',
                  fontSize: '14px'
                }
              }}
            />
            
            <Button
              variant="contained"
              onClick={applySearch}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              Search
            </Button>
            
            {searchAddress && (
              <Button
                variant="outlined"
                onClick={clearSearch}
                sx={{
                  borderColor: '#6c757d',
                  color: '#6c757d',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  textTransform: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#5a6268',
                    backgroundColor: '#f8f9fa'
                  }
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Table Section */}
      <Paper
        elevation={2}
        sx={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <AdminFundSubscriberTable 
          subscribers={filteredSubscribers}
          loading={loading}
          onUpdate={fetchFundSubscribers}
        />
      </Paper>
    </Box>
  );
};

export default FundSubscriber;
